import React from 'react';
import { Children, useTheme } from 'neon-design-system';
import { createTheme, CssBaseline, ThemeProvider } from '@material-ui/core';

export const ThemeWrapper = ({ children }: { children: Children }) => {
  const { theme } = useTheme();
  const NDS_FIX = {
    [`.ag-theme-balham-dark .ag-group-checkbox:not(.ag-invisible)+.ag-group-value:not(:empty),
    .ag-theme-balham-dark .ag-group-checkbox:not(.ag-invisible)~.ag-cell-value:not(:empty),
    .ag-theme-balham-dark .ag-group-checkbox:not(.ag-invisible)~.ag-group-checkbox,
    .ag-theme-balham-dark .ag-group-contracted:not(.ag-hidden)+.ag-group-value:not(:empty),
    .ag-theme-balham-dark .ag-group-contracted:not(.ag-hidden)~.ag-cell-value:not(:empty),
    .ag-theme-balham-dark .ag-group-contracted:not(.ag-hidden)~.ag-group-checkbox,
    .ag-theme-balham-dark .ag-group-expanded:not(.ag-hidden)+.ag-group-value:not(:empty),
    .ag-theme-balham-dark .ag-group-expanded:not(.ag-hidden)~.ag-cell-value:not(:empty),
    .ag-theme-balham-dark .ag-group-expanded:not(.ag-hidden)~.ag-group-checkbox,
    .ag-theme-balham-dark .ag-selection-checkbox:not(.ag-hidden)+.ag-group-value:not(:empty),
    .ag-theme-balham-dark .ag-selection-checkbox:not(.ag-hidden)~.ag-cell-value:not(:empty),
    .ag-theme-balham-dark .ag-selection-checkbox:not(.ag-hidden)~.ag-group-checkbox`]: {
      marginLeft: '0px'
    },
    '.ag-theme-balham-dark .ag-cell-wrapper > *:not(.ag-cell-value):not(.ag-group-value)': {
      display: 'flex',
      alignItems: 'center'
    }
  };

  const extendedTheme = createTheme({
    ...theme,
    overrides: {
      ...theme.overrides,
      MuiCssBaseline: {
        ...theme.overrides!.MuiCssBaseline,
        '@global': {
          ...theme.overrides!.MuiCssBaseline!['@global'],
          '.ag-theme-balham-dark .ag-root-wrapper': {
            margin: theme.spacing(0, 0, 2.5),
            border: 0,
            background: 'transparent',
            color: theme.themeColors.grey[200]
          },
          '.ag-theme-balham-dark .ag-header': {
            background: theme.themeColors.grey[800]
          },
          '.ag-theme-balham-dark .ag-header-cell': {
            color: theme.themeColors.grey[400]
          },
          '.ag-theme-balham-dark .ag-header-cell:last-child::after': {
            display: 'block'
          },
          '.ag-row-group.ag-row': {
            background: `${theme.themeColors.blueGrey[700]} !important`
          },
          '.ag-row-group.ag-row-level-0, .ag-row-group.ag-row-level-1': {
            borderTop: `4px solid ${theme.palette.background.default} !important`,
            fontSize: '12px',
            fontWeight: 700,
            color: theme.themeColors.grey[200]
          },
          '.ag-row-group.ag-row-level-0 .ag-cell-wrapper.ag-row-group .ag-group-checkbox': {
            display: 'none'
          },
          '.ag-row-group.ag-row-level-1': {
            background: `${theme.themeColors.grey[700]} !important`
          },
          '.ag-group-value': {
            lineHeight: '32px !important'
          },
          '.ag-cell': {
            lineHeight: '30px !important'
          },
          '.ag-theme-balham-dark .ag-group-contracted, .ag-theme-balham-dark .ag-group-expanded': {
            position: 'relative',
            left: theme.spacing(-0.5),
            marginRight: `${theme.spacing(-0.5)}px !important`
          },
          '.ag-theme-balham-dark .ag-ltr .ag-row-group-leaf-indent': {
            marginLeft: `${theme.spacing(2.75)}px !important`
          },
          '.ag-cell-wrapper.ag-row-group': {
            alignItems: 'center',
            color: theme.themeColors.grey[200]
          },
          '.ag-group-child-count': {
            display: 'none!important'
          },
          '.ag-theme-balham-dark .fake-checkbox': {
            borderColor: theme.themeColors.grey[500]
          },
          '.ag-theme-balham-dark .ag-checkbox-input-wrapper:focus-within, .ag-theme-balham-dark .ag-checkbox-input-wrapper:active': {
            boxShadow: 'none'
          },
          '.ag-theme-balham-dark .ag-cell.negative': {
            color: theme.palette.error.light
          },
          '.ag-theme-balham-dark .isMfl .ag-group-checkbox': {
            display: 'none !important'
          },
          '.ag-theme-balham-dark .ag-row-selected .ag-row-group': {
            color: 'inherit'
          },
          '.ag-theme-balham-dark .ag-row-even.ag-row-level-2.ag-row-selected': {
            background: '#1A1A1A'
          },
          '.ag-theme-balham-dark .ag-row-odd.ag-row-level-2.ag-row-selected': {
            background: '#1E1E1E'
          },
          '.ag-selection-checkbox.ag-invisible': {
            visibility: 'initial !important',
            opacity: 0.3
          },
          '.ag-row-group.ag-row-level-1.ag-row-group-expanded .ag-checkbox-input-wrapper': {
            '&.ag-checked': {
              backgroundColor: theme.themeColors.grey[500],

              '&:after': {
                border: 'none',
                content: `url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDdINFY5SDEyVjdaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo=") !important`,
                color: theme.themeColors.grey[500],
                top: 0,
                left: 0,
                position: 'absolute',
                fontSize: theme.typography.fontSize
              }
            }
          },
          '.ag-row-group.ag-row-level-1.ag-row-group-expanded.ag-row-selected .ag-checkbox-input-wrapper, .ag-theme-balham-dark .ag-checkbox-input-wrapper': {
            backgroundColor: theme.themeColors.grey[800],

            '&:after': {
              content: '"" !important',
              border: `${theme.spacing(0.25)}px solid ${theme.themeColors.grey[500]}`,
              width: '100%',
              height: '100%',
              borderRadius: theme.spacing(0.25)
            },

            '&.ag-checked': {
              backgroundColor: theme.themeColors.primary[500],

              '&:after': {
                border: 'none',
                content: `url("data:image/svg+xml,%3Csvg viewBox='0 0 32 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='white' d='M14.3 2.3L5 11.6 1.7 8.3c-.4-.4-1-.4-1.4 0-.4.4-.4 1 0 1.4l4 4c.2.2.4.3.7.3.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4-.4-.4-1-.4-1.4 0z'/%3E%3C/svg%3E") !important`,
                color: theme.themeColors.common.white,
                position: 'absolute',
                top: theme.spacing(0.5),
                left: theme.spacing(0.5),
                fontSize: theme.typography.fontSize
              }
            },

            '&.ag-indeterminate': {
              backgroundColor: theme.themeColors.grey[500],

              '&:after': {
                border: 'none',
                content: `url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='white' d='M15 7H1c-.6 0-1 .4-1 1s.4 1 1 1h14c.6 0 1-.4 1-1s-.4-1-1-1z'/%3E%3C/svg%3E") !important`,
                top: theme.spacing(-0.5),
                left: theme.spacing(0.5),
                width: '50%',
                // fix align icon for IE
                '@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none)': {
                  top: 0
                },
                // fix align of icon for EDGE
                '@supports (-ms-ime-align:auto)': {
                  top: theme.spacing(-1)
                }
              }
            }
          },
          ...NDS_FIX
        } as any
      }
    }
  });

  return (
    <ThemeProvider theme={extendedTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};
